import type {
  ExtendedConstraints,
  CapabilityConfig,
  EventHandlerCb,
} from '../../types';
import { hasOnlyKeysFromSet } from '../../utils';

export const id = 'app-events';

export enum Events {
  ProviderJoined = 'app-events:provider-joined',
  ProviderLeft = 'app-events:provider-left',
  SessionEnded = 'app-events:session-ended',
  LeavePostcall = 'app-events:leave-postcall',
}

/**
 * Base payload for all app events containing the session identifier.
 */
interface AppEventPayload {
  /**
   * The ID of the session this event relates to.
   */
  sessionId: string;
}

export type ProviderJoinedPayload = AppEventPayload;
export type ProviderLeftPayload = AppEventPayload;
export type SessionEndedPayload = AppEventPayload & {
  /**
   * Indicates if the session was on hold when it ended.
   */
  sessionWasOnHold?: boolean;
};
export type LeavePostcallPayload = AppEventPayload;

export enum Actions {}

export type Config = CapabilityConfig & {
  type: typeof id;
  events: Events[];
};

export type Constraints = ExtendedConstraints<{
  events: Events;
  actions: Actions;
  config: Config;
  eventPayloadTuple:
    | [Events.ProviderJoined, ProviderJoinedPayload]
    | [Events.ProviderLeft, ProviderLeftPayload]
    | [Events.SessionEnded, SessionEndedPayload]
    | [Events.LeavePostcall, LeavePostcallPayload];
  eventHandlerTuple:
    | [Events.ProviderJoined, EventHandlerCb<ProviderJoinedPayload>]
    | [Events.ProviderLeft, EventHandlerCb<ProviderLeftPayload>]
    | [Events.SessionEnded, EventHandlerCb<SessionEndedPayload>]
    | [Events.LeavePostcall, EventHandlerCb<LeavePostcallPayload>];
}>;

const VALID_CONFIG_KEYS = ['type', 'events'] as const;
export function isValidConfig(config: CapabilityConfig): config is Config {
  if (!hasOnlyKeysFromSet(config, VALID_CONFIG_KEYS)) {
    return false;
  }

  if (config.type !== id) {
    return false;
  }

  if (!Array.isArray(config['events'])) {
    return false;
  }

  return config['events'].every((e) => Object.values(Events).includes(e));
}
